import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Stack, Box, Text, Select, Card, Spinner, Flex } from '@sanity/ui'
import { useClient } from 'sanity'

interface BuyerGuide {
  _id: string
  title: string
  fitmentTypes: Array<{
    _key: string
    fitment: {
      name: string
    }
    listingItems: Array<{
      _key: string
      wheelSize: {
        front: {
          diameter: { wheelDiameter: number }
          width: { name: string }
          offset: { name: string }
        }
        rear?: {
          diameter: { wheelDiameter: number }
          width: { name: string }
          offset: { name: string }
        }
        sameAsFront: boolean
      }
    }>
  }>
}

interface LivePreviewProps {
  template: string
}

/**
 * LivePreview component - shows rendered template with real data
 */
export function LivePreview({ template }: LivePreviewProps) {
  const client = useClient({ apiVersion: '2024-01-01' })

  // Selection state
  const [buyerGuides, setBuyerGuides] = useState<BuyerGuide[]>([])
  const [selectedGuideId, setSelectedGuideId] = useState<string>('')
  const [selectedFitmentIndex, setSelectedFitmentIndex] = useState<number>(0)
  const [selectedListingIndex, setSelectedListingIndex] = useState<number>(0)

  // Loading/error state
  const [loadingGuides, setLoadingGuides] = useState(true)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [error, setError] = useState<string>('')

  // Preview result
  const [rendered, setRendered] = useState<string>('')
  const [listingInfo, setListingInfo] = useState<{
    fitmentTypeName: string
    wheelSpecs: { front: string; rear: string }
  } | null>(null)

  // Debounce timer ref
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  // Fetch buyer guides on mount
  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const result = await client.fetch<BuyerGuide[]>(`
          *[_type == "buyerGuide" && defined(fitmentTypes)] | order(title asc) [0...50] {
            _id,
            title,
            fitmentTypes[] {
              _key,
              "fitment": fitment-> { name },
              listingItems[] {
                _key,
                wheelSize {
                  front {
                    "diameter": diameter{ wheelDiameter },
                    "width": width->{ name },
                    "offset": offset{ name }
                  },
                  rear {
                    "diameter": diameter{ wheelDiameter },
                    "width": width->{ name },
                    "offset": offset{ name }
                  },
                  sameAsFront
                }
              }
            }
          }
        `)
        setBuyerGuides(result || [])
        if (result && result.length > 0) {
          setSelectedGuideId(result[0]._id)
        }
      } catch (err) {
        console.error('Error fetching buyer guides:', err)
        setError('Failed to load buyer guides')
      } finally {
        setLoadingGuides(false)
      }
    }
    fetchGuides()
  }, [client])

  // Reset fitment/listing selection when guide changes
  useEffect(() => {
    setSelectedFitmentIndex(0)
    setSelectedListingIndex(0)
  }, [selectedGuideId])

  // Reset listing selection when fitment changes
  useEffect(() => {
    setSelectedListingIndex(0)
  }, [selectedFitmentIndex])

  // Get current selections
  const selectedGuide = buyerGuides.find((g) => g._id === selectedGuideId)
  const fitmentTypes = selectedGuide?.fitmentTypes || []
  const selectedFitment = fitmentTypes[selectedFitmentIndex]
  const listings = selectedFitment?.listingItems || []

  // Format wheel spec for display
  const formatWheelSpec = (ws: any) => {
    if (!ws) return 'N/A'
    const diameter = ws.diameter?.wheelDiameter || '?'
    const width = String(ws.width?.name || '?').replace(/"$/, '')
    const offset = String(ws.offset?.name || '?').replace(/^ET/i, '')
    return `${diameter}" x ${width}" ET${offset}`
  }

  // Render preview (debounced)
  const renderPreview = useCallback(async () => {
    if (!selectedGuideId || !template) {
      setRendered('')
      setListingInfo(null)
      return
    }

    setLoadingPreview(true)
    setError('')

    const baseURL = process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'
    try {
      const response = await fetch(`${baseURL}/api/ai/render-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template,
          buyerGuideId: selectedGuideId,
          fitmentTypeIndex: selectedFitmentIndex,
          listingIndex: selectedListingIndex,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.message || 'Failed to render template')
      }

      const data = await response.json()
      setRendered(data.rendered)
      setListingInfo(data.listing)
    } catch (err: any) {
      console.error('Error rendering template:', err)
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setError(`Cannot connect to server. Make sure the frontend server is running at ${baseURL}`)
      } else {
        setError(err.message || 'Error rendering template')
      }
      setRendered('')
      setListingInfo(null)
    } finally {
      setLoadingPreview(false)
    }
  }, [selectedGuideId, selectedFitmentIndex, selectedListingIndex, template])

  // Debounced render on template/selection change
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      renderPreview()
    }, 500)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [renderPreview])

  if (loadingGuides) {
    return (
      <Flex align="center" justify="center" padding={4}>
        <Spinner />
        <Text size={1} style={{ marginLeft: '8px' }}>Loading buyer guides...</Text>
      </Flex>
    )
  }

  return (
    <Stack gap={3} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Selectors */}
      <Flex gap={2} wrap="wrap">
        <Box style={{ flex: 1, minWidth: '150px' }}>
          <Text size={0} weight="semibold" style={{ marginBottom: '4px', display: 'block' }}>
            Buyer Guide
          </Text>
          <Select
            value={selectedGuideId}
            onChange={(e) => setSelectedGuideId(e.currentTarget.value)}
          >
            {buyerGuides.length === 0 ? (
              <option value="">No buyer guides available</option>
            ) : (
              buyerGuides.map((guide) => (
                <option key={guide._id} value={guide._id}>
                  {guide.title}
                </option>
              ))
            )}
          </Select>
        </Box>

        <Box style={{ flex: 1, minWidth: '120px' }}>
          <Text size={0} weight="semibold" style={{ marginBottom: '4px', display: 'block' }}>
            Fitment Type
          </Text>
          <Select
            value={selectedFitmentIndex.toString()}
            onChange={(e) => setSelectedFitmentIndex(parseInt(e.currentTarget.value, 10))}
            disabled={fitmentTypes.length === 0}
          >
            {fitmentTypes.length === 0 ? (
              <option value="0">No fitment types</option>
            ) : (
              fitmentTypes.map((ft, idx) => (
                <option key={ft._key || idx} value={idx}>
                  {ft.fitment?.name || `Type ${idx + 1}`}
                </option>
              ))
            )}
          </Select>
        </Box>

        <Box style={{ flex: 1, minWidth: '150px' }}>
          <Text size={0} weight="semibold" style={{ marginBottom: '4px', display: 'block' }}>
            Listing
          </Text>
          <Select
            value={selectedListingIndex.toString()}
            onChange={(e) => setSelectedListingIndex(parseInt(e.currentTarget.value, 10))}
            disabled={listings.length === 0}
          >
            {listings.length === 0 ? (
              <option value="0">No listings</option>
            ) : (
              listings.map((listing, idx) => {
                const ws = listing.wheelSize
                const front = formatWheelSpec(ws?.front)
                const rear = ws?.sameAsFront ? front : formatWheelSpec(ws?.rear)
                return (
                  <option key={listing._key || idx} value={idx}>
                    {front}{front !== rear ? ` / ${rear}` : ''}
                  </option>
                )
              })
            )}
          </Select>
        </Box>
      </Flex>

      {/* Preview Output */}
      <Card
        padding={3}
        radius={2}
        style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#f9f9f9',
          border: '1px solid #eee',
          minHeight: '120px',
        }}
      >
        {loadingPreview ? (
          <Flex align="center" justify="center" style={{ height: '100%' }}>
            <Spinner />
            <Text size={1} style={{ marginLeft: '8px' }}>Rendering...</Text>
          </Flex>
        ) : error ? (
          <Text size={1} style={{ color: '#d9534f' }}>{error}</Text>
        ) : !rendered ? (
          <Text size={1} muted>
            {template ? 'Select a buyer guide, fitment type, and listing to preview' : 'Enter a template to see preview'}
          </Text>
        ) : (
          <Stack gap={2}>
            {listingInfo && (
              <Text size={0} muted>
                {listingInfo.fitmentTypeName}: {listingInfo.wheelSpecs.front}
                {listingInfo.wheelSpecs.front !== listingInfo.wheelSpecs.rear && ` / ${listingInfo.wheelSpecs.rear}`}
              </Text>
            )}
            <Text
              size={1}
              style={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'system-ui, sans-serif',
                lineHeight: 1.5,
              }}
            >
              {rendered}
            </Text>
          </Stack>
        )}
      </Card>
    </Stack>
  )
}

export default LivePreview
